import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { menuItems } from './navigation';

type LayoutContextValue = {
  hasRightSidebar: boolean;
  contentPadding: string; // CSS length, e.g. '1.5rem' (Tailwind p-6)
  setContentPadding: (padding: string) => void;
};

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

/**
 * Sprint 31, story-159/160/161. Full-page routes that fall under a `rightSidebar` menu item's
 * path prefix (e.g. `/projects/new` under `/projects`) but must render without the sidebar's
 * padding — the wizard and the AI review experience both need the full viewport.
 */
const FULL_WIDTH_ROUTES = ['/projects/new', '/templates/generate'];
const FULL_WIDTH_ROUTE_PATTERNS = [/^\/projects\/[^/]+\/plan\/ai$/];

function isFullWidthRoute(path: string): boolean {
  return FULL_WIDTH_ROUTES.includes(path) || FULL_WIDTH_ROUTE_PATTERNS.some((re) => re.test(path));
}

export const useLayout = (): LayoutContextValue => {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return ctx;
};

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [contentPadding, setContentPadding] = useState<string>('1.5rem');

  const hasRightSidebar = useMemo(() => {
    const path = location.pathname || '';
    if (isFullWidthRoute(path)) return false;
    return menuItems.some(
      (item) => Boolean(item.rightSidebar) && item.link && path.startsWith(item.link),
    );
  }, [location.pathname]);

  const value: LayoutContextValue = {
    hasRightSidebar,
    contentPadding,
    setContentPadding,
  };

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};
